import React from 'react'

import { observer } from 'mobx-react-lite'

import { format } from 'date-fns/format'

import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'

import { Icon, P, Text } from '@mxenabled/mxui'

// TODO : Import Timeline Components from core when they are available
import Timeline from '@mui/lab/Timeline'
import { timelineItemClasses } from '@mui/lab/TimelineItem'

import { useGlobalCopyStore } from '../../../common'
import { HealthScoreReport } from '../../../core'

import { getPercentFromRatio } from '../../util/finstrongUtils'

import ScoreHistoryTimelineItem from './ScoreHistoryTimelineItem'
import { buildDynamicCopy } from '../../../core/utils/Localization'

interface ScoreHistoryTimelineProps {
  report: HealthScoreReport
}

const ScoreHistoryTimeline: React.FC<ScoreHistoryTimelineProps> = ({ report }) => {
  const { finstrong: copyContent } = useGlobalCopyStore()
  const { score_history_drawer_timeline: timelineCopy } = copyContent

  const {
    health_score_point_difference: score,
    newer_credit_score: newCreditScore,
    older_credit_score: oldCreditScore,
    older_spend_to_income_ratio: oldSpendToIncomeRatio,
    newer_spend_to_income_ratio: newSpendToIncomeRatio,
    older_health_score: oldHealthScore,
    newer_health_score: newHealthScore,
    newer_score_calculated_at: newScoreCalculatedAt,
  } = report

  const formattedDate = format(new Date(newScoreCalculatedAt), 'MMM d')

  const getBackgroundColor = () => {
    if (score === 0) return 'info.lighter'
    return score > 0 ? 'success.lighter' : 'error.lighter'
  }

  const getMainContent = () => {
    if (score === 0) {
      return buildDynamicCopy(timelineCopy.stayed_at, oldHealthScore) as string
    }
    return score > 0 ? timelineCopy.less_spending : timelineCopy.more_spending
  }

  const getSubContent = () => {
    if (score === 0) return null

    const copyKey = score > 0 ? timelineCopy.up_from : timelineCopy.down_from
    const text = buildDynamicCopy(copyKey, oldHealthScore, newHealthScore) as string

    return (
      <>
        <Icon
          fill={true}
          name="circle"
          sx={{ color: 'text.secondary', fontSize: 8, mx: 4, p: 1 }}
        />
        {text}
      </>
    )
  }

  return (
    <Card>
      <Box
        sx={{
          borderBottomColor: 'divider',
          borderBottomStyle: 'solid',
          borderBottomWidth: score !== 0 ? 1 : 0,
          p: 16,
        }}
      >
        <Stack
          flexDirection="row"
          sx={{ alignItems: 'center', gap: 16, height: 48, width: '100%' }}
        >
          <Stack
            sx={{
              alignItems: 'center',
              backgroundColor: getBackgroundColor(),
              borderRadius: 999,
              height: 48,
              justifyContent: 'center',
              width: 48,
            }}
          >
            <P sx={{ fontWeight: 600 }} variant="body1">
              {score > 0 ? `+${score}` : score}
            </P>
          </Stack>
          <Stack sx={{ gap: 2, width: '80%' }}>
            <Text fontWeight={600} variant="body1">
              {getMainContent()}
            </Text>
            <Text sx={{ color: 'text.secondary' }} variant="subtitle2">
              {formattedDate} {getSubContent()}
            </Text>
          </Stack>
        </Stack>
      </Box>
      {score !== 0 && (
        <Box sx={{ color: 'text.secondary', pb: 8, pl: 32, pr: 16, pt: 12 }}>
          <Timeline
            sx={{
              [`& .${timelineItemClasses.root}:before`]: {
                flex: 0,
                padding: 0,
              },
              m: 0,
              p: 0,
            }}
          >
            <ScoreHistoryTimelineItem
              content={
                buildDynamicCopy(
                  timelineCopy.spend_to_income_description,
                  getPercentFromRatio(oldSpendToIncomeRatio),
                  getPercentFromRatio(newSpendToIncomeRatio),
                ) as string
              }
            />
            <ScoreHistoryTimelineItem
              content={
                buildDynamicCopy(
                  timelineCopy.credit_score_description,
                  oldCreditScore,
                  newCreditScore,
                ) as string
              }
              islast={true}
            />
          </Timeline>
        </Box>
      )}
    </Card>
  )
}

export default observer(ScoreHistoryTimeline)
